import type { ReactNode } from "react";
import { Bot, MessageCircle } from "lucide-react";
import { cn } from "@/modern/utils";
import { Badge } from "@/modern/components/ui/badge";
import type { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import type { SubmissionSource } from "@/bryntum-lib/models/Submission";
import { sourceLabel } from "../../utils/sourceLabel";
import type { LinkedSubmissionFile } from "../../hooks/useSubmissionFiles";

const SOURCE_STYLES: Record<
  SubmissionSource | "default",
  { className: string; icon: typeof MessageCircle }
> = {
  ai_intake: {
    className: "border-violet-500/35 bg-violet-500/12 text-violet-300 dark:text-violet-300",
    icon: Bot,
  },
  contact: {
    className: "border-sky-500/35 bg-sky-500/12 text-sky-300 dark:text-sky-300",
    icon: MessageCircle,
  },
  default: {
    className: "border-border/70 bg-muted/60 text-muted-foreground",
    icon: MessageCircle,
  },
};

export function SourceBadge({
  source,
  size = "sm",
  showIcon = true,
  className,
}: {
  source: string | null | undefined;
  size?: "sm" | "xs";
  showIcon?: boolean;
  className?: string;
}) {
  const key =
    source === "ai_intake" || source === "contact" ? source : "default";
  const { className: styleClass, icon: Icon } = SOURCE_STYLES[key];

  return (
    <Badge
      variant="outline"
      className={cn(
        "shrink-0 gap-1 border font-medium",
        size === "xs" ? "px-1.5 py-0 text-[10px]" : "px-2 py-0.5 text-[11px]",
        styleClass,
        className,
      )}
    >
      {showIcon && <Icon className={size === "xs" ? "size-2.5" : "size-3"} aria-hidden />}
      {sourceLabel(source)}
    </Badge>
  );
}

function leadInitials(name: string | null | undefined): string {
  const trimmed = name?.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

const AVATAR_PALETTES = [
  "bg-sky-500/20 text-sky-300 ring-sky-500/25",
  "bg-violet-500/20 text-violet-300 ring-violet-500/25",
  "bg-emerald-500/20 text-emerald-300 ring-emerald-500/25",
  "bg-amber-500/20 text-amber-300 ring-amber-500/25",
  "bg-rose-500/20 text-rose-300 ring-rose-500/25",
];

function avatarPalette(name: string | null | undefined): string {
  const seed = (name?.trim() || "?").charCodeAt(0);
  return AVATAR_PALETTES[seed % AVATAR_PALETTES.length];
}

export function LeadAvatar({
  name,
  size = "md",
  className,
}: {
  name: string | null | undefined;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const sizeClass =
    size === "sm" ? "size-8 text-xs" : size === "lg" ? "size-12 text-base" : "size-10 text-sm";

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full font-semibold ring-1 ring-inset",
        sizeClass,
        avatarPalette(name),
        className,
      )}
      aria-hidden
    >
      {leadInitials(name)}
    </div>
  );
}

/** Raised block on the inspector canvas — matches Marketing Pages detail panels. */
export function SurfaceBlock({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-border/80 bg-muted/25 shadow-sm dark:bg-background/60",
        padded && "p-4",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function PanelSection({
  label,
  hint,
  children,
  className,
  surface = true,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
  surface?: boolean;
}) {
  return (
    <section className={cn("space-y-2", className)}>
      <div>
        <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </h4>
        {hint && (
          <p className="mt-0.5 text-xs leading-snug text-muted-foreground/75">{hint}</p>
        )}
      </div>
      {surface ? (
        <SurfaceBlock>
          <div className="space-y-3">{children}</div>
        </SurfaceBlock>
      ) : (
        <div className="space-y-3">{children}</div>
      )}
    </section>
  );
}

export function DetailField({
  label,
  children,
  icon,
}: {
  label: string;
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {icon && <span className="inline-flex shrink-0 items-center [&_svg]:size-3">{icon}</span>}
        <span className="leading-none">{label}</span>
      </p>
      <div className="text-sm leading-relaxed text-foreground">{children}</div>
    </div>
  );
}

export function submissionFileToFormattedFile(
  attachment: LinkedSubmissionFile,
): FormattedFile {
  const { submissionFile } = attachment;
  const name = attachment.fileName ?? submissionFile.name ?? `File #${submissionFile.id}`;
  const thumbnailUrl = submissionFile.thumbnailUrl?.trim() || undefined;
  const originalImageUrl = submissionFile.url?.trim() || undefined;

  return {
    id: submissionFile.id,
    name,
    thumbnailUrl,
    originalImageUrl,
    canEdit: false,
    canView: true,
    record: null,
    isCCImage: false,
  };
}
